//HERView.jsx
import { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Typography } from "@mui/material";
import HERForm from "./HERForm";
import HERTable from "./HERTable";
import HERBatches from "./HERBatches";
import HERBatchActions from "./HERBatchActions";
import { HER_ACTIONS } from "store/actions/herActions";

export const BATCH_STATE = {
  BATCH_COMPLETE: "BATCH_COMPLETE",
};

export const HERView = () => {
  const dispatch = useDispatch();
  const {
    batches,
    batchNotifications,
    isBatchesLoading,
    isNotificationsLoading,
    batchesError,
    notificationsError
  } = useSelector((state) => state.her);

  // Form state - kept in local state as it's UI-specific
  const [token, setToken] = useState("");
  const [endpointUrl, setEndpointUrl] = useState("");
  const [batchId, setBatchId] = useState("");

  // UI-specific state
  const [selectedNotifications, setSelectedNotifications] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [notificationsPerPage, setNotificationsPerPage] = useState(10);
  const [selectedBatchId, setSelectedBatchId] = useState("");
  const [isAllBatches, setIsAllBatches] = useState(true);
  const [hasBatchesBeenFetched, setHasBatchesBeenFetched] = useState(false);

  // Pagination state for batches
  const [batchesCurrentPage, setBatchesCurrentPage] = useState(0);
  const [batchesPerPage, setBatchesPerPage] = useState(10);

  // Derived state from Redux
  const currentBatchData = selectedBatchId ? batchNotifications[selectedBatchId] : null;
  const notifications = selectedBatchId && currentBatchData ? (currentBatchData?.notifications || []) : null;
  const totalNotifications = selectedBatchId && currentBatchData ? (currentBatchData?.totalCount || 0) : 0;
  const hasDataBeenFetched = selectedBatchId && currentBatchData ? currentBatchData : false;

  // Load saved values from localStorage on component mount
  useEffect(() => {
    const savedEndpointUrl = localStorage.getItem("herEndpointUrl");
    const savedToken = localStorage.getItem("herToken");
    const savedBatchId = localStorage.getItem("herBatchId");

    if (savedEndpointUrl) setEndpointUrl(savedEndpointUrl);
    if (savedToken) setToken(savedToken);
    if (savedBatchId) setBatchId(savedBatchId.trim());
  }, []);

  const handleFetchNotifications = useCallback(async (targetBatchId = null, offset = null, limit = null) => {
    // Use provided parameters or fall back to state values
    const fetchBatchId = targetBatchId || batchId;
    const fetchOffset = offset !== null ? offset : (currentPage * notificationsPerPage);
    const fetchLimit = limit !== null ? limit : notificationsPerPage;

    if (!token.trim() || !endpointUrl.trim() || !fetchBatchId.trim()) {
      dispatch(HER_ACTIONS.SET_NOTIFICATIONS_ERROR("All fields (Token, Endpoint URL, and Batch ID) are required"));
      return;
    }

    const trimmedBatchId = fetchBatchId.trim();

    dispatch(HER_ACTIONS.SET_NOTIFICATIONS_LOADING(true));
    dispatch(HER_ACTIONS.CLEAR_NOTIFICATIONS_ERROR());

    // Save values to localStorage
    localStorage.setItem("herEndpointUrl", endpointUrl);
    localStorage.setItem("herToken", token);
    localStorage.setItem("herBatchId", trimmedBatchId);

    try {
      // Server-side pagination with offset and limit
      const url = `${endpointUrl}/2.1/utility_notifications/fetch-batch/${trimmedBatchId}?offset=${fetchOffset}&limit=${fetchLimit}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Handle the actual API response format
      if (data && data.payload && data.payload.userNotificationStatusBeanList) {
        const notifications = data.payload.userNotificationStatusBeanList;
        const totalCount = data.payload.totalNotificationCount || notifications.length;

        // Replace Redux store with latest fetch
        dispatch(HER_ACTIONS.SET_BATCH_NOTIFICATIONS(trimmedBatchId, notifications, totalCount));
        setSelectedBatchId(trimmedBatchId);
        setSelectedNotifications([]);
      } else if (data && data.notifications) {
        // Fallback for different response format
        const totalCount = data.totalCount || 0;

        // Replace Redux store with latest fetch
        dispatch(HER_ACTIONS.SET_BATCH_NOTIFICATIONS(trimmedBatchId, data.notifications, totalCount));
        setSelectedBatchId(trimmedBatchId);
        setSelectedNotifications([]);
      } else {
        throw new Error("Invalid response format - expected userNotificationStatusBeanList in payload");
      }
    } catch (error) {
      console.error("Error fetching HER notifications:", error);
      const errorMessage = error.message || "Failed to fetch notifications";
      dispatch(HER_ACTIONS.SET_NOTIFICATIONS_ERROR(errorMessage));
    } finally {
      dispatch(HER_ACTIONS.SET_NOTIFICATIONS_LOADING(false));
      setCurrentPage(Math.floor(fetchOffset / fetchLimit));
    }
  }, [token, endpointUrl, batchId, currentPage, notificationsPerPage, dispatch]);

  const handleFetchBatches = useCallback(async () => {
    if (!token.trim() || !endpointUrl.trim()) {
      dispatch(HER_ACTIONS.SET_BATCHES_ERROR("Token and Endpoint URL are required"));
      return;
    }
    setHasBatchesBeenFetched(true);

    dispatch(HER_ACTIONS.SET_BATCHES_LOADING(true));
    dispatch(HER_ACTIONS.CLEAR_BATCHES_ERROR());

    // Save values to localStorage
    localStorage.setItem("herEndpointUrl", endpointUrl);
    localStorage.setItem("herToken", token);

    try {
      const url = `${endpointUrl}/v1.0/her/batches/state/${BATCH_STATE.BATCH_COMPLETE}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Handle the batches response format: { requestId: "...", payload: [...] }
      if (data && data.payload && Array.isArray(data.payload)) {
        const batchesData = data.payload;

        // Store in Redux
        dispatch(HER_ACTIONS.SET_BATCHES(batchesData));

        setSelectedNotifications([]);
        setSelectedBatchId("");
      } else if (data && Array.isArray(data)) {
        // Fallback for direct array response
        dispatch(HER_ACTIONS.SET_BATCHES(data));

        setSelectedNotifications([]);
        setSelectedBatchId("");
      } else {
        throw new Error("Invalid response format - expected payload with array of batches");
      }
    } catch (error) {
      console.error("Error fetching HER batches:", error);
      const errorMessage = error.message || "Failed to fetch batches";
      dispatch(HER_ACTIONS.SET_BATCHES_ERROR(errorMessage));
    } finally {
      dispatch(HER_ACTIONS.SET_BATCHES_LOADING(false));
      setBatchesCurrentPage(0);
    }
  }, [token, endpointUrl, batches, dispatch]);

  // Handler for viewing notifications of a specific batch (from HERBatches)
  const handleViewBatch = useCallback((batch) => {
    const trimmedBatchId = (batch.id).trim();
    setBatchId(trimmedBatchId);
    setSelectedNotifications([]);

    // Call fetch directly with batchId, offset=0, and current limit
    handleFetchNotifications(trimmedBatchId, 0, notificationsPerPage);
  }, [handleFetchNotifications, notificationsPerPage]);

  // Fetch notifications whenever pagination parameters change
  useEffect(() => {
    if (selectedBatchId && token && endpointUrl) {
      handleFetchNotifications();
    }
  }, [currentPage, notificationsPerPage]);

  return (
    <div>
      <Typography gutterBottom>
        HER Preview Form
      </Typography>

      <HERForm
        token={token}
        setToken={setToken}
        endpointUrl={endpointUrl}
        setEndpointUrl={setEndpointUrl}
        batchId={batchId}
        setBatchId={setBatchId}
        onFetchNotifications={() => handleFetchNotifications(batchId, 0, notificationsPerPage)}
        onFetchBatches={handleFetchBatches}
        selectedBatchId={selectedBatchId}
        setSelectedBatchId={setSelectedBatchId}
        isAllBatches={isAllBatches}
        setIsAllBatches={setIsAllBatches}
      />

      {/* Batches Section - shown when isAllBatches is true */}
      {isAllBatches && hasBatchesBeenFetched &&(
        <HERBatches
          batches={batches}
          onViewBatch={handleViewBatch}
          currentPage={batchesCurrentPage}
          totalBatches={batches.length}
          batchesPerPage={batchesPerPage}
          onPageChange={setBatchesCurrentPage}
          setBatchesPerPage={setBatchesPerPage}
          setCurrentPage={setBatchesCurrentPage}
          isLoading={isBatchesLoading}
          error={batchesError}
        />
      )}

      {/* Notifications Section - shown when viewing specific batch */}
      {selectedBatchId && (
        <>
          {/* Batch Actions - shown above notifications table */}
          {batchId && hasDataBeenFetched && notifications && notifications.length > 0 && (
            <HERBatchActions
              batchId={selectedBatchId}
              endpointUrl={endpointUrl}
              token={token}
            />
          )}

          <HERTable
            notifications={notifications}
            selectedNotifications={selectedNotifications}
            setSelectedNotifications={setSelectedNotifications}
            currentPage={currentPage}
            totalNotifications={totalNotifications}
            notificationsPerPage={notificationsPerPage}
            onPageChange={setCurrentPage}
            setNotificationsPerPage={setNotificationsPerPage}
            setCurrentPage={setCurrentPage}
            endpointUrl={endpointUrl}
            token={token}
            handleFetchNotifications={handleFetchNotifications}
            isLoading={isNotificationsLoading}
            error={notificationsError}
          />
        </>
      )}
    </div>
  );
};

export default HERView;
